import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
    adminCandidates,
    adminCandidatesCreated,
    adminCandidatesInterviewed,
    adminContractors,
    adminCustomers,
} from '~/acl';
import { useAuthData } from '~/auth';
import { useApplicantSearch, useCandidateSearch } from '~/components/QuickNav/useCandidateSearch';
import { useContractorSearch } from '~/components/QuickNav/useContractorSearch';
import { useCustomerSearch } from '~/components/QuickNav/useCustomerSearch';
import {
    trackQuickNavClose,
    trackQuickNavNoResults,
    trackQuickNavOpen,
    trackQuickNavSelect,
} from './analytics';
import { matchItems } from './match';
import {
    findNavigablePath,
    getNavigablePages,
    resolvePagesByPaths,
    resolveRecentItems,
    toApplicantItem,
    toCandidateItem,
    toContractorItem,
    toCustomerItem,
} from './pages';
import QuickNavModal from './QuickNavModal';
import { getRecentEntries, pushRecentCustomer, pushRecentPath, type RecentEntry } from './recent';
import { SUGGESTED_PATHS } from './suggested';
import type {
    QuickNavCloseReason,
    QuickNavItem,
    QuickNavOpenSource,
    QuickNavSection,
    QuickNavSectionId,
} from './types';
import { useQuickNavShortcut } from './useQuickNavShortcut';

type QuickNavContextValue = {
    isAvailable: boolean;
    open: (source?: QuickNavOpenSource) => void;
};

const QuickNavContext = createContext<QuickNavContextValue>({
    isAvailable: false,
    open: () => {},
});

export function useQuickNav(): QuickNavContextValue {
    return useContext(QuickNavContext);
}

export function canUseQuickNavigation(user: { isRemotely: () => boolean }): boolean {
    return user.isRemotely();
}

export function QuickNavProviderStub({
    children,
    open,
    isAvailable = true,
}: {
    children: ReactNode;
    open: (source?: QuickNavOpenSource) => void;
    isAvailable?: boolean;
}) {
    const value = useMemo(() => ({ isAvailable, open }), [isAvailable, open]);
    return <QuickNavContext.Provider value={value}>{children}</QuickNavContext.Provider>;
}

export default function QuickNavProvider({ children }: { children: ReactNode }) {
    const { user } = useAuthData();
    const location = useLocation();
    const navigate = useNavigate();
    const [show, setShow] = useState(false);
    const [query, setQuery] = useState('');
    const [recentEntries, setRecentEntries] = useState<RecentEntry[]>([]);
    const openedResultPath = useRef<string | null>(null);
    const closeReasonRef = useRef<QuickNavCloseReason>('outside');
    const trackedClose = useRef(false);
    const trackedNoResults = useRef<string | null>(null);
    const showRef = useRef(show);
    useEffect(() => {
        showRef.current = show;
    }, [show]);
    const isAvailable = canUseQuickNavigation(user);

    const pages = useMemo(() => (isAvailable ? getNavigablePages(user) : []), [isAvailable, user]);
    const allowedPaths = useMemo(() => new Set(pages.map((p) => p.path)), [pages]);
    const canSeeCustomers = isAvailable && user.hasAccess(adminCustomers);
    const customerSearch = useCustomerSearch(query, canSeeCustomers);
    const canSeeContractors = isAvailable && user.hasAccess(adminContractors);
    const contractorSearch = useContractorSearch(query, canSeeContractors);
    const canSeeCandidates =
        isAvailable &&
        user.hasAccess(adminCandidates, adminCandidatesInterviewed, adminCandidatesCreated);
    const candidateSearch = useCandidateSearch(query, canSeeCandidates);
    const applicantSearch = useApplicantSearch(query, canSeeCandidates);
    const canSearchPeople = canSeeContractors || canSeeCandidates;
    const { customers } = customerSearch;
    const { contractors } = contractorSearch;
    const { candidates } = candidateSearch;
    const { applicants } = applicantSearch;
    const pending =
        customerSearch.pending ||
        contractorSearch.pending ||
        candidateSearch.pending ||
        applicantSearch.pending;
    const failed =
        customerSearch.failed ||
        contractorSearch.failed ||
        candidateSearch.failed ||
        applicantSearch.failed;

    const open = useCallback(
        (source: QuickNavOpenSource = 'icon') => {
            if (!isAvailable || showRef.current) return;

            trackedClose.current = false;
            closeReasonRef.current = 'outside';
            trackedNoResults.current = null;
            setQuery('');
            if (user.id) {
                setRecentEntries(getRecentEntries(user.id));
            }
            trackQuickNavOpen(user, source);
            setShow(true);
        },
        [isAvailable, user],
    );

    useQuickNavShortcut(
        useCallback(() => open('shortcut'), [open]),
        isAvailable,
    );

    useEffect(() => {
        if (!isAvailable || !user.id) return;
        if (location.pathname === openedResultPath.current) return;
        openedResultPath.current = null;
        const path = findNavigablePath(location.pathname, pages);
        if (!path) return;
        setRecentEntries(pushRecentPath(user.id, path, allowedPaths));
    }, [allowedPaths, isAvailable, location.pathname, pages, user.id]);

    const pageSections: QuickNavSection[] = useMemo(() => {
        const trimmed = query.trim();
        if (trimmed) {
            return [
                {
                    id: 'results' as const,
                    title: 'Results',
                    items: matchItems(pages, trimmed),
                },
            ];
        }

        const recent = resolveRecentItems(pages, recentEntries, canSeeCustomers);
        const suggested = resolvePagesByPaths(pages, SUGGESTED_PATHS);

        return [
            ...(recent.length > 0
                ? [{ id: 'recent' as const, title: 'Recent', items: recent }]
                : []),
            ...(suggested.length > 0
                ? [{ id: 'suggested' as const, title: 'Suggested', items: suggested }]
                : []),
        ];
    }, [canSeeCustomers, pages, query, recentEntries]);

    const customerSection: QuickNavSection = useMemo(
        () => ({
            id: 'customers',
            title: 'Customers',
            items: customers.map(toCustomerItem),
        }),
        [customers],
    );

    const contractorSection: QuickNavSection = useMemo(
        () => ({
            id: 'contractors',
            title: 'Contractors',
            items: contractors.map(toContractorItem),
        }),
        [contractors],
    );

    const candidateSection: QuickNavSection = useMemo(
        () => ({
            id: 'candidates',
            title: 'Candidates',
            items: candidates.map(toCandidateItem),
        }),
        [candidates],
    );

    const applicantSection: QuickNavSection = useMemo(
        () => ({
            id: 'applicants',
            title: 'Applicants',
            items: applicants.map(toApplicantItem),
        }),
        [applicants],
    );

    const sections: QuickNavSection[] = useMemo(
        () => [
            ...pageSections,
            customerSection,
            contractorSection,
            candidateSection,
            applicantSection,
        ],
        [applicantSection, candidateSection, contractorSection, customerSection, pageSections],
    );

    useEffect(() => {
        const trimmed = query.trim();
        if (
            !trimmed ||
            customers.length > 0 ||
            contractors.length > 0 ||
            candidates.length > 0 ||
            applicants.length > 0 ||
            matchItems(pages, trimmed).length > 0
        ) {
            trackedNoResults.current = null;
            return;
        }
        if (pending || failed || trackedNoResults.current === trimmed) return;
        trackedNoResults.current = trimmed;
        trackQuickNavNoResults(user, canSearchPeople ? undefined : trimmed);
    }, [
        applicants,
        canSearchPeople,
        candidates,
        contractors,
        customers,
        failed,
        pages,
        pending,
        query,
        user,
    ]);

    const handleClose = useCallback(() => {
        if (!trackedClose.current) {
            trackQuickNavClose(user, closeReasonRef.current);
            trackedClose.current = true;
        }
        setQuery('');
        trackedNoResults.current = null;
        setShow(false);
    }, [user]);

    const handleSelect = useCallback(
        (item: QuickNavItem, sectionId: QuickNavSectionId) => {
            if (item.user) {
                openedResultPath.current = item.path;
            } else if (user.id && item.customer) {
                openedResultPath.current = item.path;
                setRecentEntries(pushRecentCustomer(user.id, item.customer));
            } else if (user.id) {
                setRecentEntries(pushRecentPath(user.id, item.path, allowedPaths));
            }
            trackQuickNavSelect(user, {
                path: item.path,
                section: sectionId,
                ...(!item.user && { title: item.label, query: query.trim() || undefined }),
            });
            closeReasonRef.current = 'navigate';
            void navigate(item.path);
            handleClose();
        },
        [allowedPaths, handleClose, navigate, query, user],
    );

    const value = useMemo(
        () => ({
            isAvailable,
            open,
        }),
        [isAvailable, open],
    );

    return (
        <QuickNavContext.Provider value={value}>
            {children}
            {isAvailable && (
                <QuickNavModal
                    show={show}
                    sections={sections}
                    inputValue={query}
                    hideEmptyState={pending || failed}
                    onInputChange={setQuery}
                    onSelect={handleSelect}
                    onClose={handleClose}
                />
            )}
        </QuickNavContext.Provider>
    );
}
